/**
 * Review Queue Page
 * 
 * Lists all assignments that require human review
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';

interface ReviewItem {
  id: string;
  student_name: string | null;
  student_class: string | null;
  grade_level: string;
  created_at: string;
  pipeline_runs: {
    id: string;
    status: string;
    questions_detected: number | null;
    questions_flagged: number | null;
    created_at: string;
  }[];
}

export function ReviewQueuePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReviewQueue();
  }, [user]);

  const loadReviewQueue = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('assignments')
      .select(`
        id,
        student_name,
        student_class,
        grade_level,
        created_at,
        pipeline_runs (
          id,
          status,
          questions_detected,
          questions_flagged,
          created_at
        )
      `)
      .eq('teacher_id', user.id)
      .eq('status', 'review')
      .order('created_at', { ascending: false });

    if (fetchError) {
      setError('無法載入審核列表');
      console.error('Error loading review queue:', fetchError);
    } else {
      setItems((data as ReviewItem[]) || []);
    }

    setLoading(false);
  };

  const getPriorityChip = (flaggedCount: number | null, totalCount: number | null) => {
    if (!flaggedCount || !totalCount) {
      return <Chip label="未知" size="small" />;
    }

    const ratio = flaggedCount / totalCount;

    if (ratio > 0.5) {
      return (
        <Chip
          icon={<ErrorIcon />}
          label="高優先"
          color="error"
          size="small"
        />
      );
    } else if (ratio > 0.2) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="中優先"
          color="warning"
          size="small"
        />
      );
    } else {
      return (
        <Chip
          icon={<CheckIcon />}
          label="低優先"
          color="success"
          size="small"
        />
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-HK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        審核列表
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        以下作業需要人工審核，請檢查並確認評分結果
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="h6">沒有待審核的作業</Typography>
          <Typography variant="body2" color="text.secondary">
            所有作業都已完成審核
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>學生</TableCell>
                <TableCell>班級</TableCell>
                <TableCell>年級</TableCell>
                <TableCell>題目數</TableCell>
                <TableCell>需審核</TableCell>
                <TableCell>優先級</TableCell>
                <TableCell>提交時間</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const pipelineRun = item.pipeline_runs?.[0];
                return (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      {item.student_name || '未知學生'}
                    </TableCell>
                    <TableCell>
                      {item.student_class || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip label={item.grade_level} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {pipelineRun?.questions_detected ?? '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={pipelineRun?.questions_flagged ?? 0}
                        color="warning"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {getPriorityChip(
                        pipelineRun?.questions_flagged ?? null,
                        pipelineRun?.questions_detected ?? null
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="查看詳情">
                        <IconButton
                          color="primary"
                          onClick={() => navigate(`/review/${item.id}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

